import { Material } from '../types'
import {
  FileText, Clock, CheckCircle, AlertCircle, Loader2, Trash2,
  Brain, Map, FileCheck, ChevronRight,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface MaterialCardProps {
  material: Material
  onDelete?: (materialId: string) => void
}

const statusConfig = {
  uploaded: {
    label: 'Subido',
    bg: 'rgba(99,102,241,0.1)',
    color: '#818CF8',
    icon: Clock,
    animate: false,
  },
  processing: {
    label: 'Procesando',
    bg: 'rgba(251,191,36,0.1)',
    color: '#FBBF24',
    icon: Loader2,
    animate: true,
  },
  ready: {
    label: 'Listo',
    bg: 'rgba(16,185,129,0.1)',
    color: '#34D399',
    icon: CheckCircle,
    animate: false,
  },
  error: {
    label: 'Error',
    bg: 'rgba(244,63,94,0.1)',
    color: '#FB7185',
    icon: AlertCircle,
    animate: false,
  },
}

const contentStatusColors = {
  pending:    { color: '#475569', icon: Clock,        animate: false },
  processing: { color: '#FBBF24', icon: Loader2,      animate: true  },
  ready:      { color: '#10B981', icon: CheckCircle,  animate: false },
  failed:     { color: '#F43F5E', icon: AlertCircle,  animate: false },
}

const contentBadges = [
  { key: 'summary_status', icon: FileCheck, label: 'Resumen' },
  { key: 'quiz_status',    icon: Brain,     label: 'Quiz'    },
  { key: 'mindmap_status', icon: Map,       label: 'Mapa'    },
] as const

export default function MaterialCard({ material, onDelete }: MaterialCardProps) {
  const navigate = useNavigate()
  const cfg = statusConfig[material.status as keyof typeof statusConfig] ?? statusConfig.uploaded
  const StatusIcon = cfg.icon
  const isClickable = material.status === 'ready'

  const handleClick = () => {
    if (isClickable) navigate(`/study/${material.materialId}`)
  }

  return (
    <div
      className={`glass-card p-5 group ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
      style={isClickable ? undefined : { opacity: material.status === 'error' ? 0.7 : 1 }}
    >
      <div className="flex items-start gap-4">
        {/* File icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.06)' }}
        >
          <FileText className="w-5 h-5" style={{ color: '#64748B' }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate" style={{ color: '#E2E8F0' }}>
            {material.title}
          </h3>

          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {material.metadata?.subject && (
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: 'rgba(79,70,229,0.12)', color: '#818CF8' }}
              >
                {material.metadata.subject}
              </span>
            )}
            <span className="text-xs" style={{ color: '#475569' }}>
              {new Date(material.createdAt).toLocaleDateString('es-ES', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </span>
            {material.metadata?.pages && (
              <span className="text-xs" style={{ color: '#475569' }}>
                · {material.metadata.pages} pág.
              </span>
            )}
          </div>

          {/* Content status badges */}
          {material.status === 'ready' && (
            <div className="flex items-center gap-3 mt-3">
              {contentBadges.map(({ key, icon: Icon, label }) => {
                const rawStatus = material[key]
                if (!rawStatus) return null
                const s = contentStatusColors[rawStatus]
                const SIcon = s.icon
                return (
                  <div key={key} className="flex items-center gap-1">
                    <Icon className="w-3.5 h-3.5" style={{ color: '#475569' }} />
                    <SIcon
                      className={`w-3 h-3 ${s.animate ? 'animate-spin' : ''}`}
                      style={{ color: s.color }}
                    />
                    <span className="text-xs" style={{ color: '#64748B' }}>{label}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right: status + delete */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: cfg.bg, color: cfg.color }}
          >
            <StatusIcon className={`w-3.5 h-3.5 ${cfg.animate ? 'animate-spin' : ''}`} />
            {cfg.label}
          </span>

          {isClickable && (
            <ChevronRight
              className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: '#4F46E5' }}
            />
          )}

          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(material.materialId) }}
              className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              style={{ color: '#64748B' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(244,63,94,0.1)'
                e.currentTarget.style.color = '#F43F5E'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = '#64748B'
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
